import { isIsccStandard, isIsccPlusStandard } from './isccStandards';
import { buildDefaultAuditOutputTemplate } from './auditOutputTemplateDefaults';

const TOKEN_PATTERN = /\{\{\s*([^}]+)\s*\}\}/g;

export const parseFormDataEntry = (entry) => {
  if (!entry) return {};
  if (entry.formDataJson && typeof entry.formDataJson === 'object') return entry.formDataJson;
  if (typeof entry.formDataJson === 'string') {
    try {
      return JSON.parse(entry.formDataJson);
    } catch (error) {
      return {};
    }
  }
  return {};
};

export const formatDisplayDate = (value) => {
  if (!value) return '-';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const toList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const coalesce = (...values) => values.find((value) => value !== undefined && value !== null && value !== '');

const valueAtPath = (source, path) => String(path || '')
  .split('.')
  .filter(Boolean)
  .reduce((current, key) => (current == null ? undefined : current[key]), source);

const pick = (source, paths, fallback = '') => {
  const pathList = Array.isArray(paths) ? paths : [paths];
  return coalesce(...pathList.map((path) => valueAtPath(source, path)), fallback);
};

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return Object.values(value).filter((item) => item && typeof item === 'object');
  return [];
};

const normalizeStandards = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return item;
        return item?.standardName || item?.standard_name || item?.name || item?.label || item?.code || '';
      })
      .filter(Boolean);
  }
  return toList(value);
};

// Re-exported so existing importers keep working; the implementation is shared.
export { isIsccStandard, isIsccPlusStandard };

const buildScopeStatement = (standards, scope) => {
  const rawScope = String(scope || '').trim();
  if (!rawScope) return '';
  const standardName = Array.isArray(standards) && standards.length > 0 ? String(standards[0] || '').trim() : '';
  if (!standardName) return rawScope;
  const lowerScope = rawScope.toLowerCase();
  const lowerStandard = standardName.toLowerCase();
  if (lowerScope.startsWith(lowerStandard) || lowerScope.includes(' for ')) {
    return rawScope;
  }
  return `${standardName} for ${rawScope}`;
};

export const resolveToken = (source, tokenPath) => {
  const pathParts = String(tokenPath || '')
    .split('.')
    .map((part) => part.trim())
    .filter(Boolean);

  const result = pathParts.reduce((current, key) => {
    if (current == null) return undefined;
    return current[key];
  }, source);

  if (Array.isArray(result)) {
    return result.join(', ');
  }
  return result ?? '';
};

export const applyTemplateTokens = (text, source) => String(text || '').replace(TOKEN_PATTERN, (_, token) => {
  const resolved = resolveToken(source, token);
  return resolved == null || resolved === '' ? '' : String(resolved);
});

export const buildAuditOutputRuntimeData = ({
  template,
  project = {},
  projectContacts = {},
  certificateForm = {},
  certificateInfoForm = {},
  summaryData = null,
  siteLocations = [],
  auditSchedules = [],
  selectedScopeLabel = '',
}) => {
  const normalizedTemplate = buildDefaultAuditOutputTemplate(template);
  const formStandards = normalizeStandards(pick(certificateForm, [
    'applicableStandards',
    'applicable_standards',
    'standards',
    'standardNames',
    'standard_names',
  ], []));
  const standards = formStandards.length > 0
    ? formStandards
    : normalizedTemplate.standards;

  const certificateSites = [
    ...normalizeArray(siteLocations)
      .filter((site) => String(
        pick(site, ['siteName', 'site_name', 'name', 'locationName', 'location_name'])
        || pick(site, ['address', 'siteAddress', 'site_address']),
      ).trim())
      .map((site) => ({
        name: pick(site, ['siteName', 'site_name', 'name', 'locationName', 'location_name'], '-'),
        address: pick(site, ['address', 'siteAddress', 'site_address', 'locationAddress', 'location_address'], '-'),
        scope: pick(site, ['scope', 'siteScope', 'site_scope'], selectedScopeLabel || pick(certificateForm, ['certificateScope', 'certificate_scope', 'scope_of_certification'], '-')),
        source: 'audit-scheduling',
      })),
    ...(normalizeArray(pick(certificateInfoForm, ['additionalLocations', 'additional_locations', 'sites'], []))
      .filter((item) => String(
        pick(item, ['siteFunction', 'site_function', 'siteName', 'site_name', 'name'])
        || pick(item, ['scope', 'siteScope', 'site_scope']),
      ).trim())
      .map((item) => ({
        name: pick(item, ['siteFunction', 'site_function', 'siteName', 'site_name', 'name'], '-'),
        address: pick(item, ['address', 'siteAddress', 'site_address'], project?.locationName || project?.address || '-'),
        scope: pick(item, ['scope', 'siteScope', 'site_scope'], '-'),
        source: 'certificate-info-form',
      }))),
  ];

  const normalizeSiteIdentity = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  const sitesRepresentSameLocation = (left, right) => {
    const sameName = normalizeSiteIdentity(left?.name) && normalizeSiteIdentity(left?.name) === normalizeSiteIdentity(right?.name);
    if (!sameName) return false;
    const leftAddress = normalizeSiteIdentity(left?.address);
    const rightAddress = normalizeSiteIdentity(right?.address);
    const sameScope = !left?.scope || !right?.scope || normalizeSiteIdentity(left.scope) === normalizeSiteIdentity(right.scope);
    // FAPP-06 stores the selected site/function and scope, while scheduling
    // stores the address. Treat those two records as the same site when the
    // name matches and one source has no independent address.
    return sameScope && (!leftAddress || !rightAddress || leftAddress === rightAddress);
  };
  const dedupedSites = certificateSites.reduce((sites, site) => {
    const existingIndex = sites.findIndex((existing) => sitesRepresentSameLocation(existing, site));
    if (existingIndex === -1) return [...sites, site];
    const existing = sites[existingIndex];
    const merged = {
      ...existing,
      address: existing.address && existing.address !== '-' ? existing.address : site.address,
      scope: existing.scope && existing.scope !== '-' ? existing.scope : site.scope,
    };
    return sites.map((item, index) => (index === existingIndex ? merged : item));
  }, []);

  const reportData = summaryData || {
    summaryNarrative: pick(certificateForm, ['summaryNarrative', 'summary_narrative', 'auditSummary', 'audit_summary'], `Audit for ${project?.companyName || '-'} under ${selectedScopeLabel || pick(certificateForm, ['certificateScope', 'certificate_scope'], '-')} is ready for review.`),
    conclusion: pick(certificateForm, ['conclusion', 'certificationDecision', 'certification_decision'], 'Pending'),
    recommendations: pick(certificateForm, ['recommendations', 'recommendation', 'remarks', 'notes'], '-'),
  };

  const isIscc = standards.some(isIsccStandard);
  const isIsccPlus = standards.some(isIsccPlusStandard);
  const issueDate = isIscc
    ? pick(certificateForm, ['valid_from_start_date', 'validFrom', 'valid_from'], '')
    : pick(certificateForm, ['issueDate', 'issue_date', 'issuedDate', 'issued_date'], '');
  const expiredDate = isIscc
    ? pick(certificateForm, ['valid_from_end_date', 'validTo', 'valid_to'], '')
    : pick(certificateForm, ['expiredDate', 'expired_date', 'expiryDate', 'expiry_date'], '');
  // The certificate's own Certificate Scope wins; the scheduling scope label is only a
  // fallback for a form that has none. This was the other way round, so the certificate
  // printed the CRM service label (e.g. "Sustainability / EU / Certification #Indonesia")
  // and ignored the scope the operator actually selected -- and it disagreed with
  // `certificate.scope` a few lines below, which already had this precedence right.
  const rawScope = pick(certificateForm, [
    'certificateScope',
    'certificate_scope',
    'scopeOfCertification',
    'scope_of_certification',
    'scope',
  ], selectedScopeLabel || '');
  const scopeStatement = buildScopeStatement(
    standards,
    rawScope,
  );

  return {
    organization: {
      name: 'CBQA Global Indonesia',
      tagline: 'Management System Certification Body',
    },
    project: {
      id: project?.id || '',
      projectCode: project?.projectCode || '',
      companyName: project?.companyName || project?.clientName || '',
      clientName: project?.clientName || project?.companyName || '',
      address: pick(certificateInfoForm, ['address', 'companyAddress', 'company_address', 'clientAddress', 'client_address'], project?.locationName || project?.address || ''),
      locationName: project?.locationName || '',
      scopeLabel: selectedScopeLabel || '',
    },
    certificate: {
      certificateNumber: pick(certificateForm, ['certificateNumber', 'certificate_number', 'certNumber', 'cert_number'], project?.projectCode || ''),
      scope: pick(certificateForm, ['certificateScope', 'certificate_scope', 'scopeOfCertification', 'scope_of_certification', 'scope'], selectedScopeLabel || ''),
      verificationOfScope: pick(certificateForm, ['verificationOfScope', 'verification_of_scope', 'subScope', 'sub_scope'], ''),
      applicableForSubScope: pick(certificateForm, ['applicableForSubScope', 'applicable_for_sub_scope'], ''),
      standards,
      initialDate: formatDisplayDate(pick(certificateForm, ['initialDate', 'initial_date', 'validFrom', 'valid_from'])),
      latestCertificationDate: formatDisplayDate(pick(certificateForm, ['latestCertificationDate', 'latest_certification_date', 'latestDate', 'latest_date'])),
      issueDate: formatDisplayDate(issueDate),
      expiredDate: formatDisplayDate(expiredDate),
      certificationDecision: pick(certificateForm, ['certificationDecision', 'certification_decision', 'decision'], 'GRANT'),
      signatureName: pick(certificateForm, ['signatureName', 'signature_name', 'approvedBy', 'approved_by', 'reviewedBy', 'reviewed_by', 'producedBy', 'produced_by'], ''),
      signatureTitle: pick(certificateForm, ['signatureTitle', 'signature_title', 'approverTitle', 'approver_title'], 'Certification Manager'),
      approvedBy: pick(certificateForm, ['approvedBy', 'approved_by'], ''),
      reviewedBy: pick(certificateForm, ['reviewedBy', 'reviewed_by'], ''),
      producedBy: pick(certificateForm, ['producedBy', 'produced_by'], ''),
      remarks: pick(certificateForm, ['remarks', 'notes', 'comment', 'comments'], ''),
      scopeStatement,
      statementOfApplicability: pick(certificateForm, ['statementOfApplicability', 'statement_of_applicability', 'soa'], ''),
      // The certificate has ONE material appendix. For ISCC PLUS that appendix IS the
      // Annex I table, so Annex I rows replace the regular Material rows when supplied
      // (pinned by the tests in this file's "uses explicit Annex I rows" cases); for
      // EU/CORSIA/Japan-FIT the regular Material rows are used. This selection was always
      // correct -- what was broken was `isIsccPlus`, which required an ISCC token AND
      // "plus" and so never matched the bare "PLUS" label CRM stores, meaning Annex I
      // rows were ignored for exactly the certificates that need them.
      isccMaterials: normalizeArray(isIsccPlus
        ? pick(certificateForm, ['annex1Materials', 'annex_1_materials', 'isccAnnex1Materials'],
          pick(certificateForm, ['isccMaterials', 'iscc_materials', 'sustainableMaterials', 'sustainable_materials'], []))
        : pick(certificateForm, ['isccMaterials', 'iscc_materials', 'sustainableMaterials', 'sustainable_materials'], [])),
      // Carried through unmerged as well, so a future PLUS template with a genuinely
      // separate Annex I table has the rows available without another data change.
      annex1Materials: isIsccPlus
        ? normalizeArray(pick(certificateForm, ['annex1Materials', 'annex_1_materials', 'isccAnnex1Materials'], []))
        : [],
      isccAnnex1Required: isIsccPlus,
      ghgEmissionReductions: pick(certificateForm, ['ghgEmissionReductions', 'ghg_emission_reductions', 'ghgOption', 'ghg_option'], ''),
      footerLeft: `${project?.projectCode || ''} ${standards.join(', ')}`.trim(),
      siteCount: dedupedSites.length,
      sites: dedupedSites,
      contactPerson: pick(certificateInfoForm, ['contact', 'contactPerson', 'contact_person'], projectContacts?.certificate?.person || ''),
      contactEmail: pick(certificateInfoForm, ['email', 'contactEmail', 'contact_email'], projectContacts?.certificate?.email || ''),
      direction: pick(certificateInfoForm, ['direction', 'directions'], ''),
    },
    report: {
      ...reportData,
      auditTeam: Array.isArray(reportData?.auditTeam) ? reportData.auditTeam : auditSchedules,
    },
  };
};
